import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowLeft, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { useIntake } from "../context/IntakeContext";

export const scrollToSection = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const SectionLink = ({ hash, children, onClick }) => (
  <Link
    to={`/#${hash}`}
    onClick={onClick}
    className="text-sm font-medium text-[#3f3f46] hover:text-[#111827] transition-colors"
  >
    {children}
  </Link>
);

export const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const intake = useIntake();
  const onPricing = location.pathname === "/pricing";

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed top-0 left-0 right-0 z-50 bg-[#FFFFFF]/90 backdrop-blur-md border-b border-[#E5E7EB]"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" aria-label="No Lawyer Lawsuit home">
            <Logo src="/logo-courthouse.png" markSize={42} />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {onPricing ? (
              <Link
                to="/"
                data-testid="navbar-back-link"
                className="flex items-center gap-2 text-sm font-medium text-[#3f3f46] hover:text-[#111827] transition-colors"
              >
                <ArrowLeft size={16} /> Back
              </Link>
            ) : (
              <>
                <SectionLink hash="why">Why We Exist</SectionLink>
                <SectionLink hash="problem">The Problem</SectionLink>
                <Link
                  to="/pricing"
                  data-testid="navbar-pricing-link"
                  className="text-sm font-medium text-[#3f3f46] hover:text-[#111827] transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  to="/dashboard"
                  data-testid="navbar-my-cases-link"
                  className="text-sm font-medium text-[#3f3f46] hover:text-[#111827] transition-colors"
                >
                  My Cases
                </Link>
              </>
            )}
            <button
              data-testid="navbar-start-case-button"
              onClick={() => intake.open("letter")}
              className="group flex items-center gap-2 bg-[#111827] text-[#FFFFFF] text-sm font-semibold pl-5 pr-1.5 py-1.5 rounded-full hover:bg-[#1F2937] transition-colors"
            >
              Start Your Case
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#2563EB] text-white transition-transform duration-300 group-hover:translate-x-0.5">
                <ArrowRight size={14} strokeWidth={2.5} />
              </span>
            </button>
          </nav>

          <button
            data-testid="navbar-menu-button"
            onClick={() => setMenuOpen(true)}
            className="md:hidden p-2 -mr-2"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-[#111827] flex flex-col"
          >
            <div className="h-[68px] px-4 flex items-center justify-between border-b border-[#27272A]">
              <Logo dark src="/logo-courthouse.png" markSize={42} />
              <button
                data-testid="mobile-menu-close-button"
                onClick={() => setMenuOpen(false)}
                className="p-2 -mr-2 text-[#FFFFFF]"
                aria-label="Close menu"
              >
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {[
                { label: "Why We Exist", hash: "why" },
                { label: "The Problem", hash: "problem" },
                { label: "How It Works", hash: "how" },
              ].map((item, i) => (
                <motion.div
                  key={item.hash}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                >
                  <SectionLink hash={item.hash} onClick={() => setMenuOpen(false)}>
                    <span className="font-display text-4xl font-semibold text-[#FFFFFF] block py-3">
                      {item.label}
                    </span>
                  </SectionLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32 }}
              >
                <Link
                  to="/pricing"
                  onClick={() => setMenuOpen(false)}
                  className="font-display text-4xl font-semibold text-[#93C5FD] block py-3"
                >
                  Pricing
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.37 }}
              >
                <Link
                  to="/dashboard"
                  data-testid="mobile-menu-my-cases-link"
                  onClick={() => setMenuOpen(false)}
                  className="font-display text-4xl font-semibold text-[#FFFFFF] block py-3"
                >
                  My Cases
                </Link>
              </motion.div>
              <motion.button
                data-testid="mobile-menu-start-case-button"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => {
                  setMenuOpen(false);
                  intake.open("letter");
                }}
                className="mt-6 flex items-center justify-center gap-3 bg-[#2563EB] text-white font-bold text-lg py-4 rounded-full"
              >
                Start Your Case <ArrowRight size={20} strokeWidth={2.5} />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
